// src/elements/hudReel/hudReelControls.ts
//
// Fully refactored, leaner HUD controller for reels.
// Goals:
//   • Use AudioController as the single audio authority for reels.
//   • Keep HUD logic lightweight: minimal listeners, no suppression windows.
//   • Avoid fighting autoplay policy – HUD only reacts to user gestures.
//   • Preserve existing UX: like, CC, mute, pause/play, HUD wake, unmute nudge.

import {
  createVideoMuteButton,
  createLikeButton,
  createPausePlayButton,
  updateMuteButtonUI as updateMuteButtonVisuals,
} from "../buttons";
import { createCCButton } from "../buttons";

import { armInteractionListener } from "../../utils/mediaUtils";
import { AudioController } from "../../utils/audioController";
import { MediaUnlock } from "../../utils/mediaUnlock";
import { HudOptions } from "../hudShared";
import { LikesService, likeIdForVideo, readLiked, sendLikeUpdate } from "../../services/likes";
import { initUnmuteNudge } from "./unmuteNudge";
import { requestUnmuteFromGesture } from "./hudReelAutoplay";
import { log } from "../../utils/debug";
import { toggleLike, readCcPref, setCc } from "../../utils/hudState";

const DEBUG =
  typeof window !== "undefined" &&
  window.location.search.includes("debug");

export interface ReelControlHandles {
  getHudBtns: () => HTMLButtonElement[];
  getMuteBtn: () => HTMLButtonElement | null;
  getCcBtn: () => HTMLButtonElement | null;
  setupMuteButton: () => void;
  setupLikeButton: () => void;
  setupCcButton: () => void;
  setupPausePlayButton: () => void;
  showHudButtons: () => void;
  hideHudButtons: () => void;
  destroy: () => void;
}

/** Keep the pause/play glyph in sync with actual state.
 *  Accepts the legacy boolean (true = playing) OR a video element to derive
 *  the full three-state UI: playing → pause icon, paused-mid → play icon,
 *  paused-at-end → replay icon. */
function syncPauseIcon(
  btn: HTMLButtonElement,
  isPlayingOrVideo: boolean | HTMLVideoElement
) {
  let state: "play" | "pause" | "replay";
  let isPlaying: boolean;

  if (typeof isPlayingOrVideo === "boolean") {
    isPlaying = isPlayingOrVideo;
    state = isPlaying ? "pause" : "play";
  } else {
    const v = isPlayingOrVideo;
    isPlaying = !v.paused;
    if (isPlaying) {
      state = "pause";
    } else if (v.ended && !v.loop) {
      // Non-looping reel that finished playback → show replay icon
      state = "replay";
    } else {
      state = "play";
    }
  }

  const setter = (btn as any).__setIcon;
  if (typeof setter === "function") {
    setter(state);
  } else {
    const playIcon = btn.querySelector<SVGElement>(".icon-play");
    const pauseIcon = btn.querySelector<SVGElement>(".icon-pause");
    if (playIcon) playIcon.style.display = isPlaying ? "none" : "block";
    if (pauseIcon) pauseIcon.style.display = isPlaying ? "block" : "none";
    btn.setAttribute("aria-label", isPlaying ? "Pause video" : "Play video");
    btn.setAttribute("aria-pressed", isPlaying ? "false" : "true");
  }
  btn.classList.toggle("is-playing", isPlaying);
}

function sanitizeLikeId(s: string): string {
  try {
    const id = (s || "")
      .toLowerCase()
      .replace(/[^a-z0-9_-]+/g, "-")
      .replace(/^-+|-+$/g, "");
    return id || "reel";
  } catch {
    return "reel";
  }
}

function resolveLikeId(section: HTMLElement, video: HTMLVideoElement): string {
  try {
    const attr =
      section.getAttribute("data-like-id") ||
      section.getAttribute("data-video-id") ||
      section.getAttribute("data-reel-id") ||
      video.getAttribute("data-video-id") ||
      video.id ||
      section.id ||
      "";
    let id = sanitizeLikeId(attr);
    if (id && id !== "reel") return id;

    const src = video.currentSrc || video.src || "";
    const base = src.split(/[?#]/)[0].split("/").pop() || "";
    const stem = base.replace(/\.[a-z0-9]+$/i, "");
    id = sanitizeLikeId(stem);
    return id || "reel";
  } catch {
    return "reel";
  }
}

export function initReelControls(
  controls: HTMLElement,
  video: HTMLVideoElement,
  section: HTMLElement,
  _opts: HudOptions
): ReelControlHandles {
  // Register this reel with the central audio controller
  try {
    AudioController.registerReel(video, section);
  } catch {}

  // Managed listener collection for HUD; ensures clean teardown
  let hudDisposers: Array<() => void> = [];
  const addListener = (
    target: EventTarget,
    type: string,
    fn: EventListenerOrEventListenerObject,
    opts?: AddEventListenerOptions
  ) => {
    target.addEventListener(type, fn as EventListener, opts);
    hudDisposers.push(() =>
      target.removeEventListener(type, fn as EventListener, opts)
    );
  };

  let pausePlayBtnRef: HTMLButtonElement | null = null;
  let hudHideTimer: number | null = null;
  let onVideoPlayRef: (() => void) | null = null;
  let onVideoPauseRef: (() => void) | null = null;
  let lastWake = 0;
  let wakeQueued = false;

  try {
    armInteractionListener(video);
  } catch {}

  const HUD_TIMEOUT_MS = (() => {
    try {
      const raw = getComputedStyle(controls).getPropertyValue(
        "--hud-timeout-ms"
      );
      const n = parseInt((raw || "").trim(), 10);
      // Honor CSS var if present, but never shorter than ~7s.
      const parsed = Number.isFinite(n) && n > 0 ? n : 7000;
      return Math.max(parsed, 7000);
    } catch {
      return 7000;
    }
  })();

  const getHudBtns = (): HTMLButtonElement[] =>
    Array.from(
      controls.querySelectorAll<HTMLButtonElement>(
        ".cc-button, .mute-button, .like-button"
      )
    );

  const getMuteBtn = (): HTMLButtonElement | null =>
    controls.querySelector<HTMLButtonElement>(".mute-button");
  const getCcBtn = (): HTMLButtonElement | null =>
    controls.querySelector<HTMLButtonElement>(".cc-button");

  const applyHudVisibility = (visible: boolean) => {
    controls.classList.toggle("hud-active", visible);
    section.classList.toggle("hud-active", visible);
  };

  const hideHudNow = () => applyHudVisibility(false);

  const showHudNow = () => applyHudVisibility(true);

  const syncCcUi = () => {
    const ccBtn = getCcBtn();
    if (!ccBtn) return;
    const p = typeof readCcPref === "function" ? !!readCcPref() : false;
    (ccBtn as any).__setActive?.(p);
    ccBtn.setAttribute("aria-pressed", p ? "true" : "false");
  };

  const wakeHud = () => {
    const now = Date.now();
    if (now - lastWake < 150) return; // debounce rapid wake calls
    lastWake = now;
    showHudNow();
    syncCcUi();
    if (hudHideTimer) clearTimeout(hudHideTimer);
    hudHideTimer = window.setTimeout(() => {
      hideHudNow();
    }, HUD_TIMEOUT_MS);
  };

  const scheduleHudWake = () => {
    if (wakeQueued) return;
    wakeQueued = true;
    requestAnimationFrame(() => {
      wakeQueued = false;
      wakeHud();
    });
  };

  const stopTap = (el: Element | null) => {
    if (!el) return;
    const stop = (e: Event) => e.stopPropagation();
    addListener(el, "click", stop, { capture: false });
    addListener(el, "pointerdown", stop, { capture: false });
  };

  function wrapInStack(el: HTMLElement): HTMLElement {
    const stack = document.createElement("div");
    stack.className = "button-stack";
    stack.appendChild(el);
    return stack;
  }

  /* ------------------ Like Button ------------------ */
  const setupLikeButton = () => {
    if (controls.querySelector<HTMLButtonElement>(".like-button")) return;

    const btn = createLikeButton() as HTMLButtonElement;
    btn.classList.add("hud-btn", "like-button");

    const stack = wrapInStack(btn);
    stack.classList.add("always-visible");
    stack.setAttribute("data-hot-when-hidden", "1");
    btn.setAttribute("data-hot-when-hidden", "1");

    const counterEl = document.createElement("span");
    counterEl.className = "like-count";
    counterEl.setAttribute("aria-live", "polite");
    counterEl.setAttribute("aria-atomic", "true");
    counterEl.setAttribute("role", "status");
    counterEl.setAttribute("aria-busy", "true");
    counterEl.textContent = "0";
    counterEl.style.transition = "opacity 200ms ease";
    counterEl.style.opacity = "0";
    stack.appendChild(counterEl);

    controls.insertAdjacentElement("afterbegin", stack);

    const videoId = likeIdForVideo(video, { scope: "domain+filename" });

    const bumpCount = () => {
      counterEl.classList.remove("bump");
      void counterEl.offsetWidth;
      counterEl.classList.add("bump");
      window.setTimeout(() => counterEl.classList.remove("bump"), 260);
    };

    const syncButtonState = (liked: boolean) => {
      btn.classList.toggle("liked", liked);
      btn.setAttribute("aria-pressed", liked ? "true" : "false");
      btn.setAttribute("aria-label", liked ? "Unlike this video" : "Like this video");
    };

    syncButtonState(readLiked(videoId));

    (async () => {
      try {
        const count = await LikesService.getCount(videoId);
        if (typeof count === "number") {
          counterEl.textContent = String(Math.max(0, count));
          counterEl.style.opacity = "1";
        }
      } catch {
        counterEl.style.opacity = "0";
      } finally {
        counterEl.setAttribute("aria-busy", "false");
      }
    })();

    btn.addEventListener("click", async (e) => {
      e.stopPropagation();
      const next = !readLiked(videoId);
      syncButtonState(next);

      const flashHook = (btn as any).__flash;
      if (typeof flashHook === "function") {
        try {
          flashHook(next ? +1 : -1);
        } catch {}
      }

      try {
        const { count } = await sendLikeUpdate(videoId, next);
        if (typeof count === "number") {
          counterEl.textContent = String(Math.max(0, count));
          counterEl.style.opacity = "1";
          bumpCount();
        }
      } catch {
        syncButtonState(!next);
      }

      toggleLike(section);
      scheduleHudWake();
    });
  };

  /* ------------------ Mute Button ------------------ */

  function reflectMuteStateInHud(
    muteBtn: HTMLButtonElement,
    section: HTMLElement,
    video: HTMLVideoElement
  ) {
    const isEffectivelyMuted = video.muted || video.volume === 0;
    section.setAttribute("data-muted", isEffectivelyMuted ? "1" : "0");
    updateMuteButtonVisuals(muteBtn, isEffectivelyMuted);
  }

  const setupMuteButton = () => {
    if (getMuteBtn()) return;

    const muteEl = createVideoMuteButton(video.muted) as HTMLButtonElement;
    muteEl.classList.add(
      "mute-button",
      "video-mute-button",
      "reel-mute-button"
    );
    muteEl.dataset.variant = "video";

    const stack = wrapInStack(muteEl);

    const likeStack = controls
      .querySelector(".button-stack .like-button")
      ?.closest(".button-stack");
    if (likeStack) {
      likeStack.insertAdjacentElement("afterend", stack);
    } else {
      controls.insertAdjacentElement("afterbegin", stack);
    }

    stopTap(muteEl);

    // Initial UI sync
    reflectMuteStateInHud(muteEl, section, video);

    const onMuteClick = async (e: Event) => {
      e.stopPropagation();
      const currentlyMuted = video.muted || video.volume === 0;
      log("[HUD] Mute button clicked", {
        beforeMuted: currentlyMuted,
        volume: video.volume,
      });

      if (currentlyMuted) {
        // User is trying to unmute with a gesture; delegate to the reel autoplay gate.
        const ok = await requestUnmuteFromGesture(video, section);

        if (ok) {
          try {
            section.dataset.userAllowed = "1";
          } catch {}
          try {
            AudioController.setAudioAllowed(true);
          } catch {}
        } else {
          // If we failed, keep it muted to avoid inconsistent UI
          try {
            AudioController.requestMute(video);
          } catch {}
        }
      } else {
        // User wants to mute
        try {
          AudioController.requestMute(video);
        } catch {}

        // Explicitly reflect mute state and broadcast to any global listeners
        try {
          video.muted = true;
          video.setAttribute("muted", "");
        } catch {}
        try {
          section.setAttribute("data-muted", "1");
        } catch {}
        try {
          document.dispatchEvent(
            new CustomEvent("sc:mutechange", { detail: { muted: true, section, video } })
          );
        } catch {}
      }

      reflectMuteStateInHud(muteEl, section, video);
      scheduleHudWake();
    };

    addListener(muteEl, "click", onMuteClick as EventListener);

    // Keep HUD in sync when video volume/mute changes from elsewhere
    addListener(
      video,
      "volumechange",
      (() => {
        return () => {
          try {
            AudioController.remember(video);
          } catch {}
          reflectMuteStateInHud(muteEl, section, video);
        };
      })() as EventListener,
      { passive: true }
    );

    // Optional: react to global mute changes (e.g., narration HUD)
    const onGlobalMute = (ev: Event) => {
      const detail = (ev as CustomEvent<any>).detail || {};
      const muted = !!detail.muted;
      if (DEBUG) log("[HUD] Global mute sync (reel)", { muted });
      try {
        AudioController.syncVideoToGlobalMute(video);
      } catch {}
      reflectMuteStateInHud(muteEl, section, video);
    };
    addListener(document, "sc:mutechange", onGlobalMute as EventListener, {
      passive: true,
    });
  };

  /* ------------------ CC Button ------------------ */
  const setupCcButton = () => {
    if ((controls as any).__ccSetup) return;
    // Hard guard: only initialize once per controls instance.
    if ((controls as any).__ccInitialized) return;
    if (getCcBtn()) return;

    // Use shared factory from buttons.ts
    const pref = typeof readCcPref === "function" ? !!readCcPref() : false;
    const ccBtn = createCCButton(pref);
    ccBtn.classList.add("hud-btn"); // respect HUD fade/timeout

    const stack = wrapInStack(ccBtn as unknown as HTMLButtonElement);
    controls.appendChild(stack);

    // Persist and propagate preference/state on user toggle to avoid drift between sections.
    addListener(ccBtn as unknown as HTMLButtonElement, "click", (ev) => {
      ev.stopPropagation();
      const current = ccBtn.getAttribute("aria-pressed") === "true";
      const next = !current;
      (ccBtn as any).__setActive?.(next);
      ccBtn.setAttribute("aria-pressed", next ? "true" : "false");
      try { setCc(section, next); } catch {}
    });

    stopTap(ccBtn as unknown as HTMLButtonElement);

    const onCcChange = (() => {
      let lastState: boolean | null = null;
      return (ev: Event) => {
        const detail = (ev as CustomEvent<any>).detail || {};
        const targetSection = detail.section as HTMLElement | undefined;
        // Ignore updates meant for other sections to keep HUD-local state stable
        if (targetSection && targetSection !== section) return;

        const next =
          typeof detail.active === "boolean"
            ? detail.active
            : typeof detail.enabled === "boolean"
            ? detail.enabled
            : (ccBtn as any).getAttribute("aria-pressed") === "true";

        if (next === lastState) return;
        lastState = next;

        (ccBtn as any).__setActive(next);
        log("[HUD] CC change sync", { fromEvent: detail, next });
      };
    })();
    addListener(document, "sc:cc-change", onCcChange as EventListener, {
      passive: true,
    });

    // One-time sync with stored preference in case no event fires yet.
    queueMicrotask?.(() => {
      const p = typeof readCcPref === "function" ? !!readCcPref() : false;
      (ccBtn as any).__setActive?.(p);
      ccBtn.setAttribute("aria-pressed", p ? "true" : "false");
    });

    // Mark initialized so we never mount a duplicate CC button.
    (controls as any).__ccInitialized = true;
  };

  /* ------------------ Pause/Play Button ------------------ */
  const setupPausePlayButton = () => {
    if (controls.querySelector(".pause-button") || pausePlayBtnRef) return;

    const btn = createPausePlayButton(video.paused) as HTMLButtonElement;
    btn.className = "pause-button centered hud-btn";

    Object.assign(btn.style, {
      position: "absolute",
      top: "50%",
      left: "50%",
      transform: "translate(-50%, -50%)",
      zIndex: "1001",
    });

    try {
      const cs = getComputedStyle(controls);
      const rawHudSize = (cs.getPropertyValue("--hud-btn") || "").trim();
      const base = parseInt(rawHudSize || "44", 10) || 44;
      const target = Math.round(base * 1.25);
      btn.style.setProperty("width", `${target}px`, "important");
      btn.style.setProperty("height", `${target}px`, "important");
    } catch {
      btn.style.setProperty("width", "62px", "important");
      btn.style.setProperty("height", "62px", "important");
    }

    let iconEl = btn.querySelector(
      "svg.icon, svg, img.hud-icon, img"
    ) as SVGElement | HTMLImageElement | null;
    if (!iconEl) {
      btn.innerHTML = `
        <svg class="icon" viewBox="0 0 24 24" aria-hidden="true">
          <polygon class="icon-play" points="8,5 19,12 8,19"></polygon>
          <g class="icon-pause">
            <rect x="7" y="5" width="4" height="14"></rect>
            <rect x="13" y="5" width="4" height="14"></rect>
          </g>
        </svg>
      `;
      iconEl = btn.querySelector("svg.icon") as SVGElement | null;
    }

    if (iconEl) {
      iconEl.style.setProperty("width", "70%", "important");
      iconEl.style.setProperty("height", "70%", "important");
      (iconEl as any).style.pointerEvents = "none";
    }

    btn.setAttribute("aria-pressed", video.paused ? "true" : "false");
    btn.setAttribute("aria-label", video.paused ? "Play video" : "Pause video");
    syncPauseIcon(btn, !video.paused);

    const triggerPop = () => {
      btn.classList.remove("anim-pop");
      void btn.offsetWidth;
      btn.classList.add("anim-pop");
      window.setTimeout(() => btn.classList.remove("anim-pop"), 350);
    };

    btn.addEventListener("click", async (e) => {
      e.stopPropagation();
      try {
        if (video.paused) {
          // If the video has finished (showing the replay icon), rewind to
          // the start so play() actually restarts rather than no-oping.
          // Safari in particular needs the explicit reset.
          if (video.ended) {
            try { video.currentTime = 0; } catch {}
          }
          const audioPref = (() => {
            try {
              return AudioController.isAudioPlayable();
            } catch {
              return null;
            }
          })();

          let played = false;

          if (audioPref === null && video.currentTime === 0) {
            // First play, no stored preference: ask the AudioController to
            // unlock with audio under this user gesture.
            const ok = await AudioController.requestUnmuteFromUserGesture(video);
            played = ok && !video.paused;

            if (ok) {
              try { (window as any).__STORYCORE_USER_ACTIVATED = true; } catch {}
              try { await MediaUnlock.unlockNow("reel-gate"); } catch {}
              try { await AudioController.unlockAudio("reel-play-button"); } catch {}
              try { AudioController.setAudioAllowed(true); } catch {}
            }

            if (!ok) {
              // Fallback: play muted if the browser rejects audio.
              try {
                AudioController.requestMute(video);
                await video.play();
                try {
                  (window as any).__STORYCORE_USER_ACTIVATED = true;
                  await MediaUnlock.unlockNow("reel-gate");
                  await AudioController.unlockAudio("reel-play-button");
                  AudioController.setAudioAllowed(true);
                } catch {}
                played = !video.paused;
              } catch {}
            }
          } else if (audioPref === false) {
            // User has previously disallowed audio: play muted.
            try {
              AudioController.requestMute(video);
              await video.play();
              played = !video.paused;
            } catch {}
          } else {
            // Audio already allowed: just play and let controller
            // enforce global mute state.
            try {
              await video.play();
              played = !video.paused;
            } catch {}
          }

          const isPlayingNow = played && !video.paused;

          if (isPlayingNow) {
            try {
              // Remember the current volume for future sessions.
              if (!video.muted && video.volume === 0) {
                video.volume = AudioController.getRememberedVolume(video, 0.8);
              }
              AudioController.remember(video);
              section.dataset.userAllowed = "1";
              AudioController.setAudioAllowed(true);
            } catch {}

            const muteBtn = getMuteBtn();
            if (muteBtn) {
              reflectMuteStateInHud(muteBtn, section, video);
            }
          }

          btn.setAttribute("aria-pressed", isPlayingNow ? "false" : "true");
          btn.setAttribute(
            "aria-label",
            isPlayingNow ? "Pause video" : "Play video"
          );
          syncPauseIcon(btn, isPlayingNow);
        } else {
          video.pause();
          btn.setAttribute("aria-pressed", "true");
          btn.setAttribute("aria-label", "Play video");
          syncPauseIcon(btn, false);
        }
        triggerPop();
        scheduleHudWake();
      } catch {
        scheduleHudWake();
      }
    });

    onVideoPlayRef = () => {
      btn.setAttribute("aria-pressed", "false");
      btn.setAttribute("aria-label", "Pause video");
      syncPauseIcon(btn, true);
      triggerPop();
      scheduleHudWake();
    };
    onVideoPauseRef = () => {
      // Pass the video element so syncPauseIcon can pick replay vs play
      // based on video.ended (for non-looping reels)
      syncPauseIcon(btn, video);
      triggerPop();
      scheduleHudWake();
    };
    // Also wire `ended` and `seeking` so the icon updates immediately
    // when the video reaches its end OR when the user scrubs back from end.
    const onVideoEndedRef = () => syncPauseIcon(btn, video);
    const onVideoSeekingRef = () => syncPauseIcon(btn, video);
    video.addEventListener("play", onVideoPlayRef);
    video.addEventListener("pause", onVideoPauseRef);
    video.addEventListener("ended", onVideoEndedRef);
    video.addEventListener("seeking", onVideoSeekingRef);

    const reelFrame = section.querySelector(".reel-frame") as HTMLElement | null;
    if (reelFrame) {
      if (getComputedStyle(reelFrame).position === "static") {
        reelFrame.style.position = "relative";
      }
      reelFrame.appendChild(btn);
    } else {
      controls.appendChild(btn);
    }

    pausePlayBtnRef = btn;
    stopTap(btn);
    scheduleHudWake();
  };

  /* ------------------ Init ------------------ */
  setupLikeButton();
  setupMuteButton();
  setupCcButton();
  setupPausePlayButton();
  try {
    initUnmuteNudge({
      glowColor: "rgba(255,184,0,0.45)",
      ringColor: "rgba(255,184,0,0.40)",
    });
  } catch {}

  /* ------------------ HUD Visibility ------------------ */
  const showHudButtons = () => {
    scheduleHudWake();
  };
  const hideHudButtons = () => {
    if (hudHideTimer) {
      clearTimeout(hudHideTimer);
      hudHideTimer = null;
    }
    hideHudNow();
  };

  addListener(controls, "pointerdown", scheduleHudWake as EventListener, {
    capture: true,
  });
  addListener(controls, "click", scheduleHudWake as EventListener, { capture: true });
  addListener(controls, "mousemove", scheduleHudWake as EventListener, {
    passive: true,
  });

  const wakeZone = section.querySelector(
    ".reel-wake-zone"
  ) as HTMLElement | null;
  if (wakeZone) {
    wakeZone.style.setProperty("pointer-events", "auto", "important");
    addListener(wakeZone, "pointerdown", scheduleHudWake as EventListener);
    addListener(wakeZone, "click", scheduleHudWake as EventListener);
    addListener(wakeZone, "mousemove", scheduleHudWake as EventListener, {
      passive: true,
    });
  }

  const reelFrameEl = section.querySelector(
    ".reel-frame"
  ) as HTMLElement | null;
  if (reelFrameEl) {
    reelFrameEl.style.setProperty("pointer-events", "auto", "important");
    addListener(reelFrameEl, "pointerdown", scheduleHudWake as EventListener);
    addListener(reelFrameEl, "click", scheduleHudWake as EventListener);
    addListener(reelFrameEl, "mousemove", scheduleHudWake as EventListener, {
      passive: true,
    });
  }

  // Initial wake so HUD is visible when the reel first appears.
  scheduleHudWake();

  /* ------------------ Destroy ------------------ */
  const destroy = () => {
    if (hudDisposers.length) {
      for (const off of hudDisposers) {
        try {
          off();
        } catch {}
      }
      hudDisposers = [];
    }
    if (hudHideTimer) {
      clearTimeout(hudHideTimer);
      hudHideTimer = null;
    }
    if (pausePlayBtnRef) {
      if (onVideoPlayRef) {
        video.removeEventListener("play", onVideoPlayRef);
        onVideoPlayRef = null;
      }
      if (onVideoPauseRef) {
        video.removeEventListener("pause", onVideoPauseRef);
        onVideoPauseRef = null;
      }
    }
    try {
      AudioController.unregisterReel(video);
    } catch {}
  };

  return {
    getHudBtns,
    getMuteBtn,
    getCcBtn,
    setupMuteButton,
    setupLikeButton,
    setupCcButton,
    setupPausePlayButton,
    showHudButtons,
    hideHudButtons,
    destroy,
  };
}
